import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  //ModalFooter,
  ModalBody,
  ModalCloseButton,
  Grid,
  Icon,
  Text,
  Box,
  useDisclosure,
  Flex,
  Badge,
  //Tooltip,
  GridItem,
  Skeleton,
  useColorModeValue,
  Button,
  //SkeletonText,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  //HStack
} from "@chakra-ui/react";
import { FaStar, FaGift, FaHistory } from "react-icons/fa";
import dateUtils from "../../utils/formatDate";
import { useTranslation } from "react-i18next";

const StampCard = ({ 
  totalStamps = 0, 
  stampTransactions = [], 
  usedStamps = [], 
  expiredStamps = [],
  isLoading 
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const bgColor = useColorModeValue("gray.100", "gray.800");
  const textColor = useColorModeValue("custom.800", "custom.900");
  const { t } = useTranslation();
  const savedLanguage = localStorage.getItem("i18nextLng") || "en";

  // Initialize stamps based on totalStamps
  const stamps = Array(5)
    .fill(false)
    .map((_, index) => index < totalStamps);

  // Calculate number of completed stamp cards (every 5 stamps)
  const completedCards = Math.floor(totalStamps / 5);

  return (
    <>
      {/* Stamp Icon to open modal */}
      {/* <Tooltip label={t('view_details')} hasArrow> */}
        <Button
          mb={1}
          size="xs"
          borderRadius={'2xl'}
          variant="outline"
          px={[2, 3]}
          py={[1, 2]}
          fontSize={["2xs", "sm"]}
          colorScheme="blue"
          fontWeight="bold"
          onClick={onOpen}
          leftIcon={<FaHistory />}
        >
          {t('view_details')}
        </Button>
      {/* </Tooltip> */}
  
      {/* Stamp Card Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent mt={0} top={0}>
          <ModalHeader 
            textAlign="center"
            pt={`calc(1rem + env(safe-area-inset-top))`}
            pb="4"
            pl="4"
            pr="12" // Make room for the close button
          >
            {t('stamp_card.title')}
          </ModalHeader>
          {/* <ModalCloseButton /> */}
          <ModalCloseButton
            position="absolute"
            top={`calc(1rem + env(safe-area-inset-top))`}
            right="4"
            zIndex="1"
            color="gray"
          />
    
          <ModalBody>
            <Text mb={4} textAlign="center" fontSize="md">
              {t('stamp_card.description.earn')} <br/>
              <span dangerouslySetInnerHTML={{
                __html: t('stamp_card.description.reward')
              }} />
            </Text>
  
            {/* Stamp Grid */}
            <Grid templateColumns="repeat(3, 1fr)" gap={4} justifyItems="center" mb={6}>
              {stamps.map((isStamped, index) => (
                <Box
                  key={index}
                  w="60px"
                  h="60px"
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bg={isStamped ? "teal.100" : "white"}
                  border="2px solid"
                  borderColor={isStamped ? "teal.400" : "gray.300"}
                  boxShadow="sm"
                  transition="background 0.2s ease-in-out"
                >
                  {isStamped ? (
                    <Icon as={FaStar} color="teal.500" boxSize={6} />
                  ) : (
                    <Text fontSize="lg" color="gray.500">{index + 1}</Text>
                  )}
                </Box>
              ))}
  
              {/* Reward Box (6th Position) */}
              <Flex
                w="60px"
                h="60px"
                borderRadius="full"
                alignItems="center"
                justifyContent="center"
                bg={totalStamps >= 5 ? "pink.100" : "white"}
                border="2px solid"
                borderColor={totalStamps >= 5 ? "pink.400" : "gray.300"}
                boxShadow="sm"
              >
                <Box position="relative" display="inline-block">
                  <Icon as={FaGift} color={totalStamps >= 5 ? "pink.500" : "gray.400"} boxSize={7} />
                  {completedCards > 0 && (
                    <Badge
                      position="absolute"
                      top="-20px"
                      right="-3px"
                      bg="red.500"
                      color="white"
                      borderRadius="full"
                      px={2}
                      fontSize="xs"
                      boxShadow="md"
                    >
                      x{completedCards}
                    </Badge>
                  )}
                </Box>
              </Flex>
            </Grid>
  
            {/* Summary Stats */}
            <GridItem colSpan={4}>
               <Grid templateColumns="repeat(2, 1fr)" gap={4} py={2}>
                 <Box>
                   <Text fontSize="sm" fontWeight="bold">
                     {t('stamp_card.labels.stamps')}
                   </Text>
                   <Text color="gray.400">
                     {t('stamp_card.units.stamp_plural', { count: dateUtils.convertToBurmeseNumerals(totalStamps, savedLanguage) })}
                   </Text>
                 </Box>
                 <Box>
                   <Text fontSize="sm" fontWeight="bold">
                     {t('stamp_card.labels.rewards')}
                   </Text>
                   <Text color="gray.400">
                     {t('stamp_card.units.reward_plural', { count:  dateUtils.convertToBurmeseNumerals(completedCards, savedLanguage) })}
                   </Text>
                 </Box>
               </Grid>
            </GridItem>

            {/* Stamp History Tabs */}
            <Tabs variant="enclosed" size={'sm'} isFitted mt={"6"}>
              <TabList mb="1em">
                <Tab fontSize={'xs'}>{t('stamp_card.tabs.earned')}</Tab>
                <Tab fontSize={'xs'}>{t('stamp_card.tabs.used')}</Tab>
                <Tab fontSize={'xs'}>{t('stamp_card.tabs.expired')}</Tab>
              </TabList>
              <TabPanels>
                {/* Earned Stamps Tab */}
                <TabPanel p={0}>
                  <StampHistoryTable 
                    data={stampTransactions}
                    isLoading={isLoading}
                    status="earned"
                    textColor={textColor}
                    bgColor={bgColor}
                    savedLanguage={savedLanguage}
                    t={t}
                  />
                </TabPanel>

                {/* Used Stamps Tab */}
                <TabPanel p={0}>
                  <StampHistoryTable 
                    data={usedStamps}
                    isLoading={isLoading}
                    status="used"
                    textColor={textColor}
                    bgColor={bgColor}
                    savedLanguage={savedLanguage}
                    t={t}
                  />
                </TabPanel>

                {/* Expired Stamps Tab */}
                <TabPanel p={0}>
                  <StampHistoryTable 
                    data={expiredStamps}
                    isLoading={isLoading}
                    status="expired"
                    textColor={textColor}
                    bgColor={bgColor}
                    savedLanguage={savedLanguage}
                    t={t}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

// Stamp status colors
const statusColors = {
  earned: "green",
  used: "blue",
  expired: "red"
};

// Reusable Stamp History Table Component
const StampHistoryTable = ({ 
  data, 
  isLoading, 
  status, 
  savedLanguage, 
  t 
}) => {
  return (
    <TableContainer>
      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th>
              {status === 'used' 
                ? t('stamp_card.history.used_on') 
                : t('stamp_card.history.date')}
            </Th>
            <Th>{t('stamp_card.history.stamps')}</Th>
            <Th textAlign={'center'}>{t('stamp_card.history.status')}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Tr key={i}>
                <Td>
                  <Skeleton height="12px" mb={1} />
                  {status === 'earned' && <Skeleton height="10px" width="70%" />}
                </Td>
                <Td textAlign="center">
                  <Skeleton height="12px" width="30px" mx="auto" />
                </Td>
                <Td>
                  <Skeleton height="20px" width="60px" borderRadius="md" />
                </Td>
              </Tr>
            ))
          ) : data?.length > 0 ? (
            (status === 'expired' 
              ? [...data].sort((a, b) => new Date(b.expiration_date) - new Date(a.expiration_date))
              : data
            ).map((item, index) => (
              <Tr key={index}>
                <Td fontSize={'xs'}>
                  {dateUtils.formatDate(
                    status === 'earned' 
                      ? item.createdAt 
                      : status === 'used' 
                      ? item.createdAt 
                      : item.expiration_date, 
                    savedLanguage
                  )}
                  {status === 'earned' && (
                    <Box fontSize={'2xs'} color="gray.400" mt={1}>
                      {t('expired_on', {
                        date: dateUtils.formatDate(item.expiration_date, savedLanguage)
                      })}
                    </Box>
                  )}
                </Td>
                <Td fontSize={'xs'} textAlign="center">
                  {dateUtils.convertToBurmeseNumerals(item.stamps, savedLanguage)}
                </Td>
                <Td fontSize={'2xs'} textAlign="center">
                  <Badge px={2} py={1} borderRadius="md" colorScheme={statusColors[status]}>
                    {t(`stamp_card.status.${status}`)}
                  </Badge>
                </Td>
              </Tr>
            ))

            // data.map((item, index) => (
            //   <Tr key={index}>
            //     <Td fontSize={'xs'}>
            //       {dateUtils.formatDate(
            //         status === 'earned' 
            //           ? item.createdAt 
            //           : status === 'used' 
            //           ? item.createdAt 
            //           : item.expiration_date, 
            //         savedLanguage
            //       )}
            //       {status === 'earned' && (
            //         <Box fontSize={'2xs'} color="gray.400" mt={1}>
            //           {t('expired_on', {
            //             date: dateUtils.formatDate(item.expiration_date, savedLanguage)
            //           })}
            //         </Box>
            //       )}
            //     </Td>
            //     <Td fontSize={'xs'} textAlign="center">
            //       {dateUtils.convertToBurmeseNumerals(item.stamps, savedLanguage)}
            //     </Td>
            //     <Td fontSize={'2xs'} textAlign="center">
            //       <Badge px={2} py={1} borderRadius="md" colorScheme={statusColors[status]}>
            //         {t(`stamp_card.status.${status}`)}
            //       </Badge>
            //     </Td>
            //   </Tr>
            // ))
          ) : (
            <Tr>
              <Td colSpan={3} fontSize="xs" textAlign="center" py={4}>
                <Text fontSize="xs" color="gray.500">
                  {t('stamp_card.history.no_data')}
                </Text>
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};


export default StampCard;